import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import {
  PaymentMethodSchema,
  RefundReasonSchema,
  RefundStatusSchema,
  SchemaValidationFailedError,
} from '@kilat/shared';

/**
 * Typed wrappers around the refund callables (G8).
 *
 * Refunds are server-only for a reason worth restating at the call site: the
 * cap that stops several partial refunds adding up past what the customer paid
 * needs the archived ticket AND the sum of every other refund against it.
 * Firestore rules cannot aggregate a query, so there is no client write path —
 * this module is the only way in.
 */

// ── requestRefund ─────────────────────────────────────────────────────────

export const RequestRefundInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  ticketId: z.string().min(1),
  amountSen: z.number().int().positive(),
  reason: RefundReasonSchema,
  note: z.string().max(500).optional(),
});
export type RequestRefundInput = z.infer<typeof RequestRefundInputSchema>;

export const RequestRefundOutputSchema = z.object({
  id: z.string().min(1),
  /** Sen still refundable on that wash after this request — shown back to staff. */
  remainingAfter: z.number().int().nonnegative(),
});
export type RequestRefundOutput = z.infer<typeof RequestRefundOutputSchema>;

const requestRefundFn: HttpsCallable<RequestRefundInput, RequestRefundOutput> = httpsCallable(
  functions,
  'requestRefund',
);

export async function callRequestRefund(input: RequestRefundInput): Promise<RequestRefundOutput> {
  const parsed = RequestRefundInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('requestRefund.input', parsed.error.flatten());
  }
  const result = await requestRefundFn(parsed.data);
  const out = RequestRefundOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('requestRefund.output', out.error.flatten());
  }
  return out.data;
}

// ── decideRefund ──────────────────────────────────────────────────────────

export const DecideRefundInputSchema = z.object({
  tenantId: z.string().min(1),
  refundId: z.string().min(1),
  to: RefundStatusSchema,
  decisionNote: z.string().max(500).optional(),
  settledMethod: PaymentMethodSchema.optional(),
  settlementRef: z.string().max(120).optional(),
});
export type DecideRefundInput = z.infer<typeof DecideRefundInputSchema>;

const DecideRefundOutputSchema = z.object({ ok: z.boolean() });

const decideRefundFn: HttpsCallable<DecideRefundInput, { ok: boolean }> = httpsCallable(
  functions,
  'decideRefund',
);

export async function callDecideRefund(input: DecideRefundInput): Promise<void> {
  const parsed = DecideRefundInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('decideRefund.input', parsed.error.flatten());
  }
  const result = await decideRefundFn(parsed.data);
  const out = DecideRefundOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('decideRefund.output', out.error.flatten());
  }
}
